import type { EndRequest } from '@algolia/client-common';
import { liteClient } from 'algoliasearch/lite';
import { search } from '@src/search/search.actions';
import { AlgoliaSearchCache, type CachedSearchQuery } from '@src/utils/algoliaSearchCache';
import { isJSendError } from '@src/utils/jsend.util';

/*
 As we use a custom search client to fetch Search API response, but still rely on the Algolia SDK
 to handle the requests, caching and parsing the data, we can provide placeholder values for the Algolia client.

 We don't hit Algolia API directly, we don't need to use the actual APP ID and API KEY.

 These adapters live apart from `search.util.ts` so that pure search helpers (index names, sort, SSR
 state) stay free of the `search` server action and its transitive auth/instrumentation graph — which
 keeps them importable from unit tests and non-server contexts.
*/
import { instrumentAction } from "@src/utils/instrumentation";
const PLACEHOLDER_INIT_VALUE = 'placeholder';
function decodeSearchRequestData(data: string | Uint8Array | undefined): string {
  if (!data) return '';
  return typeof data === 'string' ? data : new TextDecoder().decode(data);
}
function parseSearchRequests(data: string | Uint8Array | undefined): CachedSearchQuery[] {
  const rawData = decodeSearchRequestData(data);
  const requests = rawData ? JSON.parse(rawData)?.requests ?? [] : [];
  return requests.map(({
    indexName,
    ...params
  }: {
    indexName: string;
    [key: string]: unknown;
  }) => ({
    indexName,
    params
  }));
}
export function createCachedSearchRequesterSend() {
  const cache = new AlgoliaSearchCache();
  return async function send(request: EndRequest) {
    const requests = parseSearchRequests(request.data);
    const results = await cache.resolve(requests, search);
    return {
      status: 200,
      content: JSON.stringify({
        results
      }),
      isTimedOut: false
    };
  };
}
export function getQuerySearchClient() {
  return liteClient(PLACEHOLDER_INIT_VALUE, PLACEHOLDER_INIT_VALUE, {
    requester: {
      send: createCachedSearchRequesterSend()
    }
  });
}

/**
 * Search client for pages that need facet results without a user
 * query (e.g. /all-workouts). Unlike `getQuerySearchClient`, this never
 * short-circuits empty queries. Every request is forwarded to the
 * Search API endpoint so Algolia can return facet values and counts.
 */
export function getCatalogSearchClient() {
  return liteClient(PLACEHOLDER_INIT_VALUE, PLACEHOLDER_INIT_VALUE, {
    requester: {
      async send(request: EndRequest) {
        const rawData = decodeSearchRequestData(request.data);
        const requests = (rawData ? JSON.parse(rawData)?.requests : [])?.map(({
          indexName,
          ...params
        }: {
          indexName: string;
          [key: string]: unknown;
        }) => ({
          indexName,
          params
        }));
        const response = await instrumentAction("SearchActions.search", search)(requests);
        if (isJSendError(response)) {
          throw new Error('message' in response ? response.message : 'Search request failed');
        }
        return {
          status: 200,
          content: JSON.stringify(response),
          isTimedOut: false
        };
      }
    }
  });
}